// src/utils/lazyPage.ts
import { FC, lazy, LazyExoticComponent } from 'react';

/**
 * A page loaded on demand (55.65): its code is a file of its own, fetched the first time the page
 * is opened, so the landing page no longer waits for the whole game (the main bundle was 1.39 MB).
 *
 * A DEPLOY REPLACES THE FILES. Hosting keeps only the current release, so a tab opened before a
 * deploy that then opens a page it has not loaded yet asks for a file that is gone. The import
 * fails; the page reloads once — the fresh index names the new files — and a flag in
 * sessionStorage stops a second failure from reloading for ever.
 */
const RELOADED = 'vomm:lazy-reload';

// Every page is a function component; FC<P> is what React.lazy needs without an `any`.
export function lazyPage<P extends object>(load: () => Promise<{ default: FC<P> }>): LazyExoticComponent<FC<P>> {
    return lazy(async (): Promise<{ default: FC<P> }> => {
        try {
            const module = await load();
            try { sessionStorage.removeItem(RELOADED); } catch { /* storage may be blocked */ }
            return module;
        } catch (error) {
            let reloaded = false;
            try { reloaded = sessionStorage.getItem(RELOADED) === '1'; } catch { /* storage may be blocked */ }
            if (!reloaded) {
                try { sessionStorage.setItem(RELOADED, '1'); } catch { /* storage may be blocked */ }
                window.location.reload();
                // Never settles: the page is going away.
                return new Promise<{ default: FC<P> }>(() => undefined);
            }
            throw error;
        }
    });
}
